import { useEffect, useMemo } from 'react';
import Step2_RemainingItemsInfo from './Step2_RemainingItemsInfo';
import Step2_RemainingItemsForm from './Step2_RemainingItemsForm';
import Step2_RemainingItemsTotals from './Step2_RemainingItemsTotals';
import HighVarianceAlert from './HighVarianceAlert';

/**
 * Renders the content for Step 2 of the prep modal, focusing on accounting for remaining items.
 *
 * @param {object} props
 * @param {object} props.palletData - The pallet data.
 * @param {object} props.formData - The form data.
 * @param {function} props.onDataChange - Function to handle data changes.
 * @param {function} props.onValidationChange - Function to handle validation changes.
 * @param {function} props.setConfirmationRequired - Function to set the confirmation required state.
 * @param {function} props.setVarianceDetails - Function to set the variance details.
 */
function PrepStep2({
    palletData,
    formData,
    onDataChange,
    onValidationChange,
    setConfirmationRequired,
    setVarianceDetails,
}) {
    const { totalUnits, unitsPerCase } = palletData;
    const { numRPCs, unitsPerRPC, looseUnits, scrapUnits, remainingCases, remainingUnits } = formData;

    const expectedRemainingUnits = useMemo(() => {
        const preppedIntoRPCs = (parseInt(numRPCs, 10) || 0) * (parseInt(unitsPerRPC, 10) || 0);
        const preppedLoose = parseInt(looseUnits, 10) || 0;
        const preppedScrap = parseInt(scrapUnits, 10) || 0;
        return totalUnits - preppedIntoRPCs - preppedLoose - preppedScrap;
    }, [totalUnits, numRPCs, unitsPerRPC, looseUnits, scrapUnits]);

    const userDeclaredTotal = useMemo(() => {
        const inCases = (parseInt(remainingCases, 10) || 0) * (parseInt(unitsPerCase, 10) || 0);
        const loose = parseInt(remainingUnits, 10) || 0;
        return inCases + loose;
    }, [remainingCases, remainingUnits, unitsPerCase]);

    const { variance, variancePercent, isHighVariance } = useMemo(() => {
        if (expectedRemainingUnits === 0) {
            // If expected remaining units is 0, then the variance is the user declared total
            // If the user declared total is greater than 10, then the variance is significant
            return { variance: userDeclaredTotal, variancePercent: '100.0%', isHighVariance: userDeclaredTotal > 10 };
        }
        const varianceValue = Math.abs(expectedRemainingUnits - userDeclaredTotal) / expectedRemainingUnits;
        return {
            variance: varianceValue,
            variancePercent: `${(varianceValue * 100).toFixed(1)}%`,
            isHighVariance: varianceValue >= 0.1,
        };
    }, [expectedRemainingUnits, userDeclaredTotal]);

    const handleDataChange = (e) => {
        const { name, value } = e.target;
        onDataChange((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    useEffect(() => {
        const requiredFieldsValid =
            formData?.remainingCases != null &&
            formData.remainingCases !== '' &&
            formData.remainingUnits != null &&
            formData.remainingUnits !== '';
        onValidationChange(requiredFieldsValid);
    }, [formData, onValidationChange]);

    useEffect(() => {
        setConfirmationRequired(isHighVariance);
        if (isHighVariance) {
            setVarianceDetails({
                variancePercent,
                expected: expectedRemainingUnits,
                userDeclaredTotal,
            });
        }
    }, [
        isHighVariance,
        setConfirmationRequired,
        setVarianceDetails,
        variancePercent,
        expectedRemainingUnits,
        userDeclaredTotal,
    ]);

    return (
        <div className="space-y-4">
            <Step2_RemainingItemsInfo expectedRemainingUnits={expectedRemainingUnits} unitsPerCase={unitsPerCase} />

            <Step2_RemainingItemsForm
                remainingCases={remainingCases}
                remainingUnits={remainingUnits}
                unitsPerCase={unitsPerCase}
                onDataChange={handleDataChange}
            />

            <Step2_RemainingItemsTotals
                remainingCases={remainingCases}
                remainingUnits={remainingUnits}
                unitsPerCase={unitsPerCase}
                userDeclaredTotal={userDeclaredTotal}
            />

            {isHighVariance && (
                <HighVarianceAlert
                    variancePercent={variancePercent}
                    expectedRemainingUnits={expectedRemainingUnits}
                    userDeclaredTotal={userDeclaredTotal}
                />
            )}
        </div>
    );
}

export default PrepStep2;
